import { dateVersIso } from '../api/mappers';

const dateIso = (d) => dateVersIso(d) || '';

/** Les jours avant la fin d'une attestation où la cloche prévient. */
export const PREAVIS_JOURS = 30;

/** Chaque attestation qu'une entreprise peut avoir à montrer. */
const ATTESTATIONS = {
  rc: {
    icon: '🛡',
    lib: 'Assurance RC entreprise',
    aide: 'Police d’assurance responsabilité civile',
  },
  avs_independant: {
    icon: '👤',
    lib: 'Statut d’indépendant (AVS)',
    aide: 'Attestation d’affiliation de la caisse AVS',
    sansFin: true,
  },
  tva: { icon: '🧾', lib: 'Attestation TVA', aide: 'Inscription au registre TVA', sansFin: true },
  poursuites: {
    icon: '⚖️',
    lib: 'Extrait de l’Office des poursuites',
    aide: 'Valable 3 mois — à refaire avant une soumission',
  },
  laa: { icon: '🩹', lib: 'Assurance accidents LAA', aide: 'Si vous avez des employés' },
  paritaire: {
    icon: '🏗',
    lib: 'Attestation commission paritaire',
    aide: 'Demandée pour les marchés publics',
  },
  registre_commerce: {
    icon: '📄',
    lib: 'Extrait du registre du commerce',
    aide: 'Si votre raison individuelle est inscrite',
    aideSociete: 'Obligatoire pour une Sàrl / SA',
    sansFin: true,
  },
  statuts: { icon: '📜', lib: 'Statuts de la société', aide: '', sansFin: true },
  acte_constitutif: {
    icon: '✍️',
    lib: 'Acte constitutif',
    aide: 'Acte notarié de création',
    sansFin: true,
  },
  registre_parts: {
    icon: '👥',
    lib: 'Registre des parts',
    libSA: 'Registre des actions',
    aide: '',
    sansFin: true,
  },
  comptes_annuels: {
    icon: '📊',
    lib: 'Comptes annuels approuvés',
    aide: 'Bilan et compte de résultat de chaque année',
    sansFin: true,
  },
  avs_employeur: { icon: '🧑‍💼', lib: 'Attestation AVS employeur', aide: 'Cotisations à jour' },
  lpp: { icon: '🏦', lib: 'Attestation LPP (2ᵉ pilier)', aide: '' },
};

/** Ce qu'une raison individuelle montre : [clé, niveau]. */
const INDEPENDANT = [
  ['rc', 'conseille'],
  ['avs_independant', 'conseille'],
  ['tva', 'conseille'],
  ['poursuites', 'conseille'],
  ['laa', 'facultatif'],
  ['paritaire', 'facultatif'],
  ['registre_commerce', 'facultatif'],
];

/** Ce qu'une Sàrl ou une SA montre : [clé, niveau]. */
const SOCIETE = [
  ['registre_commerce', 'obligatoire'],
  ['statuts', 'conseille'],
  ['acte_constitutif', 'conseille'],
  ['registre_parts', 'conseille'],
  ['comptes_annuels', 'conseille'],
  ['avs_employeur', 'conseille'],
  ['lpp', 'conseille'],
  ['rc', 'conseille'],
  ['tva', 'conseille'],
  ['poursuites', 'conseille'],
  ['laa', 'facultatif'],
  ['paritaire', 'facultatif'],
];

/**
 * La forme dont dépend la liste : « Sàrl », « SA », sinon « Indépendant »
 * (une forme pas encore choisie, ou « Autre »).
 *
 * @param {string} [forme] forme juridique choisie dans Réglages
 * @returns {'Indépendant'|'Sàrl'|'SA'} la forme de la liste
 */
export function formeDeLaListe(forme) {
  return forme === 'Sàrl' || forme === 'SA' ? forme : 'Indépendant';
}

/**
 * Les attestations attendues pour une forme d'entreprise, dans l'ordre où
 * elles se montrent.
 *
 * @param {string} [forme] « Indépendant », « Sàrl » ou « SA »
 * @returns {{cle: string, icon: string, lib: string, aide: string, niveau: string}[]} la liste
 */
export function attestationsAttendues(forme) {
  const f = formeDeLaListe(forme);
  const societe = f !== 'Indépendant';
  return (societe ? SOCIETE : INDEPENDANT).map(([cle, niveau]) => {
    const a = ATTESTATIONS[cle];
    return {
      cle,
      icon: a.icon,
      lib: f === 'SA' && a.libSA ? a.libSA : a.lib,
      aide: societe && a.aideSociete ? a.aideSociete : a.aide,
      niveau,
    };
  });
}

/** Le libellé d'une attestation, par sa clé ; une clé inconnue reste telle quelle. */
export function libelleAttestation(cle, forme) {
  const a = ATTESTATIONS[cle];
  if (!a) return cle || '';
  return formeDeLaListe(forme) === 'SA' && a.libSA ? a.libSA : a.lib;
}

/** Jours entiers de `de` à `a`, deux dates ISO. */
function joursEntre(de, a) {
  return Math.round((Date.parse(a + 'T00:00:00Z') - Date.parse(de + 'T00:00:00Z')) / 86400000);
}

/**
 * Où en est une attestation : reçue et valable, bientôt à renouveler,
 * expirée, ou pas encore là.
 *
 * @param {object} [piece] la pièce jointe (sa `date` est la date de fin)
 * @param {string} niveau « obligatoire », « conseille » ou « facultatif »
 * @param {Date} [maintenant] date de référence
 * @returns {{etat: 'ok'|'bientot'|'expiree'|'manquant'|'ajouter'|'facultatif', jours?: number, fin?: string}} l'état
 */
export function etatAttestation(piece, niveau, maintenant = new Date()) {
  if (!piece) {
    if (niveau === 'obligatoire') return { etat: 'manquant' };
    return { etat: niveau === 'facultatif' ? 'facultatif' : 'ajouter' };
  }
  // Un registre du commerce, des statuts : valables tant que l'entreprise existe.
  if (sansDateDeFin(piece.categorie)) return { etat: 'ok' };
  const fin = dateIso(piece.date);
  if (!fin) return { etat: 'ok' };
  const jours = joursEntre(maintenant.toISOString().slice(0, 10), fin);
  if (jours < 0) return { etat: 'expiree', jours, fin };
  if (jours <= PREAVIS_JOURS) return { etat: 'bientot', jours, fin };
  return { etat: 'ok', jours, fin };
}

/**
 * Les attestations à renouveler : expirées, ou qui le seront dans les
 * 30 jours. La cloche les compte. Pour chaque sorte, seule la plus récente
 * compte : l'ancienne attestation remplacée ne sonne plus.
 *
 * @param {object[]} pieces pièces de l'entreprise (genre ENTREPRISE)
 * @param {Date} [maintenant] date de référence
 * @returns {object[]} les pièces à renouveler
 */
export function attestationsARenouveler(pieces, maintenant = new Date()) {
  return dernieresParSorte(pieces).filter((p) => {
    const { etat } = etatAttestation(p, 'conseille', maintenant);
    return etat === 'bientot' || etat === 'expiree';
  });
}

/**
 * Pour chaque sorte d'attestation, celle qui finit le plus tard (à défaut,
 * la dernière déposée). Les factures jointes à l'entreprise, sans sorte
 * d'attestation, n'en sont pas.
 *
 * @param {object[]} pieces pièces de l'entreprise
 * @returns {object[]} une pièce par sorte
 */
export function dernieresParSorte(pieces) {
  const parSorte = new Map();
  (pieces || [])
    .filter((p) => p && ATTESTATIONS[p.categorie])
    .forEach((p) => {
      const deja = parSorte.get(p.categorie);
      const cle = (x) => (dateIso(x.date) || '') + String(x.id).padStart(12, '0');
      if (!deja || cle(p) > cle(deja)) parSorte.set(p.categorie, p);
    });
  return [...parSorte.values()];
}

/**
 * Vrai pour un document sans date de fin : l'extrait du registre du
 * commerce, les statuts… restent valables tant que l'entreprise existe.
 *
 * @param {string} [categorie] sorte d'attestation
 * @returns {boolean} le document ne s'arrête pas
 */
export function sansDateDeFin(categorie) {
  return Boolean(ATTESTATIONS[categorie] && ATTESTATIONS[categorie].sansFin);
}

/** Vrai pour une sorte d'attestation connue. */
export function estAttestation(categorie) {
  return Boolean(ATTESTATIONS[categorie]);
}

/** L'icône d'une attestation, par sa sorte ; 📄 pour un autre document. */
export function iconeAttestation(categorie) {
  return (ATTESTATIONS[categorie] && ATTESTATIONS[categorie].icon) || '📄';
}

/**
 * Les attestations qu'on peut envoyer à un client, une régie : la plus
 * récente de chaque sorte, dans l'ordre de la carte pour la forme de
 * l'entreprise, puis les autres documents ajoutés à la main. Une pièce sans
 * fichier ne part pas : il n'y a rien à joindre.
 *
 * @param {object[]} pieces pièces de l'entreprise (genre ENTREPRISE)
 * @param {string} [forme] forme juridique choisie dans Réglages
 * @returns {object[]} les pièces à proposer, chacune avec son fichier
 */
export function attestationsAEnvoyer(pieces, forme) {
  const ordre = [...attestationsAttendues(forme).map((a) => a.cle), ...Object.keys(ATTESTATIONS)];
  const rang = (p) => ordre.indexOf(p.categorie);
  const dernieres = dernieresParSorte(pieces).sort((a, b) => rang(a) - rang(b));
  const autres = (pieces || []).filter((p) => p && p.categorie === 'attestation');
  return [...dernieres, ...autres].filter((p) => p.fichier);
}

/**
 * Le nom d'une attestation tel que l'écran le montre : celui de sa sorte,
 * sinon le libellé saisi à son ajout.
 *
 * @param {object} piece la pièce
 * @param {string} [forme] forme juridique, pour « Registre des actions » d'une SA
 * @returns {string} le nom, en français
 */
export function nomAttestation(piece, forme) {
  if (piece && ATTESTATIONS[piece.categorie]) return libelleAttestation(piece.categorie, forme);
  return (piece && piece.libelle) || 'Attestation';
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Les clients dont le nom ou l'adresse contient ce qui est tapé, et qui ont
 * une adresse e-mail : cinq au plus, pour ne pas couvrir l'écran.
 *
 * @param {object[]} clients fiches des clients
 * @param {string} texte ce qui est tapé dans « À qui ? »
 * @returns {object[]} les clients proposés
 */
export function clientsProposes(clients, texte) {
  const t = String(texte || '')
    .trim()
    .toLowerCase();
  if (t.length < 2) return [];
  return (clients || [])
    .filter((c) => c && c.email && EMAIL.test(String(c.email).trim()))
    .filter(
      (c) =>
        String(c.nom || '')
          .toLowerCase()
          .includes(t) || String(c.email).toLowerCase().includes(t),
    )
    .filter((c) => String(c.email).trim().toLowerCase() !== t)
    .slice(0, 5);
}
